import type { LucideIcon } from 'lucide-react';
import { createElement, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * The title block every page opens with: the section's own glyph beside a 17px
 * title, the actions opposite, and on a detail screen a back link above it.
 *
 * Presentational, so both applications render the same block — the console's
 * header was a 24px bold title with the description underneath, which is a
 * different typographic scale from the product's and read as a different app.
 * Each app resolves its own glyph from its own navigation and brings its own
 * router's link, because neither belongs in a package that knows about
 * neither.
 */

export interface PageHeaderProps {
  title: string;
  /** One short line beside the title — a count, or what the page is for. */
  description?: ReactNode;
  /** The section's glyph. Each app passes the one its navigation gives the route. */
  icon?: LucideIcon;
  /** A back link, above the title. The app supplies its own router's element. */
  back?: ReactNode;
  /** Buttons and controls, right-aligned. Wrap on narrow screens. */
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  icon,
  back,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('pb-1', className)}>
      {back ? <div className="mb-2">{back}</div> : null}
      {/* The title takes what is left and never less than 12rem, so the actions
          drop to a line of their own before they squeeze the title to its first
          letter. A short description — a count — stays beside the title; a long
          one wraps under it. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
        <div className="flex min-w-48 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h1 className="flex min-w-0 items-center gap-2 self-center text-xl font-semibold text-foreground">
            {icon
              ? createElement(icon, { className: 'size-[18px] shrink-0 text-muted-foreground' })
              : null}
            <span className="truncate">{title}</span>
          </h1>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {/* On a phone the actions keep to one line and scroll sideways rather
            than stacking into a second and third row of pills. */}
        {actions ? (
          <div className="no-scrollbar flex items-center gap-2 *:shrink-0 max-sm:-mx-4 max-sm:w-[calc(100%+2rem)] max-sm:overflow-x-auto max-sm:px-4 sm:flex-wrap">
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
